'use client';

import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { logDev } from '../lib/log-dev';
import { UserRole, USER_ROLES } from '../auth/types';
import { navigateToPlatform, smartNavigate } from '../lib/platform-routing';
import { detectPlatform } from '../lib/platform-detection';
import { CUSTOMER_WEB_ROUTES, ADMIN_ROUTES } from '../constants/routes';
import { getSignOutRoute } from '../lib/role-based-routing';
import { buildAuthUrlFromCurrentPath } from '../lib/auth-login-redirect';
import Dropdown from './Dropdown';
import LocaleSelector from './LocaleSelector';

const MegaMenu = lazy(() => import('./MegaMenu').then((m) => ({ default: m.default })));
import ProfileCompletionDialog from './ProfileCompletionDialog';
import { 
  Home, 
  ShoppingBag, 
  Info, 
  Phone, 
  User, 
  Package, 
  Heart, 
  Store, 
  MapPin, 
  Users, 
  BarChart3, 
  Settings, 
  Crown, 
  Wrench, 
  Shield, 
  FileText, 
  CreditCard, 
  Handshake, 
  Truck, 
  Link as LinkIcon, 
  DollarSign,
  LogOut,
  Menu,
  X,
  Eye,
  Star,
  RefreshCw,
  Tag,
  HelpCircle,
  RotateCcw,
  MessageCircle,
  TrendingUp,
  Plus,
  Folder,
  UserCog,
  Download,
  Mail,
  Database,
  Bell,
  Clock,
  CheckSquare,
  Palette,
  Activity,
  Grid3x3
} from 'lucide-react';
import { NotificationBadge } from './notifications/NotificationBadge';
import { useNotificationBell } from './notifications/NotificationBell';
import { useDashboardSafe } from '../lib/dashboard-context';
import { WebstoreSearchBox } from './webstore/WebstoreSearchBox';
import { ThemeColorSelectorDialog } from './theme/ThemeColorSelectorDialog';
import { AdminQuickActionsDropdown } from './admin/AdminQuickActionsDropdown';
// CSS imported globally in apps/web/src/app/globals.css - removed duplicate import to prevent webpack conflicts
// import './AppHeader.css';
import './Dropdown.css';
import './MegaMenu.css';
import '../styles/selectors.css';
import { CUSTOMER_MAIN_NAV, ADMIN_MAIN_NAV } from '../constants/navigation';

interface MegaMenuItem {
  label: string;
  href?: string;
  description?: string;
  icon?: React.ReactNode;
  children?: MegaMenuItem[];
  featured?: boolean;
  badge?: string;
  isActive?: boolean;
  onClick?: () => void;
  className?: string;
}

interface AppHeaderProps {
  currentApp: 'web' | 'portal' | 'dashboard' | 'workspace';
  user?: any;
  userRole?: UserRole;
  loading?: boolean;
  onPortalMenuToggle?: () => void;
}

function AppHeaderComponent({ currentApp, user, userRole, loading = false, onPortalMenuToggle }: AppHeaderProps) {
  const { t, ready: navReady } = useTranslation('nav');
  const { t: tAuth, ready: authReady } = useTranslation('auth');
  const pathname = usePathname(); // Move usePathname to top level
  const authLoginUrl = useMemo(
    () => buildAuthUrlFromCurrentPath({ tab: 'signin' }),
    [pathname]
  );
  const authSignupUrl = useMemo(
    () => buildAuthUrlFromCurrentPath({ tab: 'signup' }),
    [pathname]
  );
  
  // # Wait for translations to load AND client-side mount to prevent hydration mismatch
  const [isMounted, setIsMounted] = useState(false);
  const translationsReady = navReady && authReady && isMounted;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]); // Track which sections are expanded (all collapsed by default)
  
  // # Mark as mounted on client-side to prevent SSR hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);
  
  // # Get notification count for badge
  const { unreadCount } = useNotificationBell();
  // Use safe version that returns defaults instead of throwing when provider is missing
  const { availableDepartments } = useDashboardSafe();

  const [isScrolled, setIsScrolled] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [hasDepartmentAccess, setHasDepartmentAccess] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check if profile needs completion
  useEffect(() => {
    // Only show dialog if user exists, has no first_name, and profile is not completed
    // This prevents showing the dialog if user provided name during signup
    if (!loading && user && !user.first_name && !user.profile_completed) {
      setShowProfileDialog(true);
    } else {
      setShowProfileDialog(false);
    }
  }, [user, loading]);

  const handleProfileComplete = (firstName: string, lastName: string) => {
    // Update the user object locally
    if (user) {
      user.first_name = firstName;
      user.last_name = lastName;
      user.profile_completed = true;
    }
    // Close the dialog immediately
    setShowProfileDialog(false);
  };

  // Get display name (first name or email)
  const getDisplayName = () => {
    if (user?.first_name) {
      return user.first_name;
    }
    return user?.email || 'User';
  };

  // # Check if user has any department assignments
  useEffect(() => {
    if (availableDepartments && availableDepartments.length > 0) {
      setHasDepartmentAccess(true);
    } else {
      setHasDepartmentAccess(false);
    }
  }, [availableDepartments]);

  // # Listen for department assignment changes
  useEffect(() => {
    const handleDepartmentChange = async () => {
      // Trigger dashboard context refresh
      if (user?.id) {
        // The dashboard context should refresh automatically via its useEffect
        // Force a small delay to allow database updates to propagate
        setTimeout(() => {
          // Dispatch event to trigger dashboard refresh
          window.dispatchEvent(new Event('refresh-departments'));
        }, 500);
      }
    };

    window.addEventListener('user-departments-changed', handleDepartmentChange);
    return () => {
      window.removeEventListener('user-departments-changed', handleDepartmentChange);
    };
  }, [user?.id]);

  // # Check if user has admin rights (via role OR department assignment)
  const hasAdminRights = () => {
    // If user has department assignments, they should have admin access
    if (hasDepartmentAccess) return true;
    
    if (!userRole) return false;
    
    const adminRoles: UserRole[] = [
      USER_ROLES.ADMIN,
      USER_ROLES.OWNER,
      USER_ROLES.MANAGER,
      USER_ROLES.DEVELOPER,
      USER_ROLES.EMPLOYEE
    ];
    
    return adminRoles.includes(userRole);
  };

  // # Memoize callbacks to prevent unnecessary re-renders
  const handleSignOut = useCallback(async () => {
    try {
      const userRole = user?.role;
      await supabase.auth.signOut();
      const signOutRoute = getSignOutRoute(userRole);
      window.location.href = signOutRoute;
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, [user?.role]);

  // # Get main navigation items with mega menu data
  const getMainNavigationItems = () => {
    if (currentApp === 'dashboard') {
      // Admin portal navigation
      return getAdminNavigationItems();
    } else {
      // Customer web store navigation
      return getCustomerNavigationItems();
    }
  };

  // # Customer navigation with mega menu
  const getCustomerNavigationItems = (): MegaMenuItem[] => CUSTOMER_MAIN_NAV as unknown as MegaMenuItem[];

  // # Get admin menu items for MegaMenu (permission-based)
  // SIMPLIFIED: Direct link to workspace with session transfer
  const getAdminMenuItems = (): MegaMenuItem[] => {
    if (!user || !hasAdminRights()) return [];

    const handleWorkspaceClick = () => {
      logDev.info('[AppHeader] Workspace navigation clicked', {
        action: 'redirect_to_workspace',
        target: '/workspace'
      });
      
      // Direct path-based navigation - NO subdomains
      // /workspace route exists and handles the redirect to admin dashboard
      window.location.href = '/workspace';
    };

    const menuItems: MegaMenuItem[] = [
      {
        label: '🚀 Åpne Workspace',
        description: 'Gå til arbeidsområdet og administrasjon',
        icon: <Shield size={16} />,
        featured: true,
        onClick: handleWorkspaceClick
      }
    ];

    return menuItems;
  };

  // # Admin navigation with mega menu
  const getAdminNavigationItems = (): MegaMenuItem[] => ADMIN_MAIN_NAV as unknown as MegaMenuItem[];

  // # Get My Account dropdown items for mobile drawer
  const getMyAccountItems = (): any[] => {
    if (!user) {
      return [
        { label: 'Logg Inn', href: authLoginUrl, icon: <User size={16} /> },
        { label: 'Registrer Deg', href: authSignupUrl, icon: <User size={16} /> }
      ];
    }

    const baseAccountItems: any[] = [
      { label: 'Min Profil', href: getHref('/profile', '/portal/profile', '/profile'), icon: <User size={16} /> },
      { label: 'Mine Bestillinger', href: '/account/orders', icon: <Package size={16} /> },
      { label: 'Ønskeliste', href: getHref('/wishlist', '/portal/wishlist', '/wishlist'), icon: <Heart size={16} /> },
      { label: 'Adresser', href: getHref('/addresses', '/portal/addresses', '/addresses'), icon: <MapPin size={16} /> },
      { divider: true },
      { label: 'Logg Ut', onClick: handleSignOut, icon: <LogOut size={16} />, className: 'logout' }
    ];

    // Add role-specific items
    if (userRole === USER_ROLES.PREMIUM_CUSTOMER) {
      baseAccountItems.splice(3, 0, { label: 'Premium Funksjoner', href: getHref('/premium', '/portal/premium', '/premium'), icon: <Crown size={16} /> });
    }

    return baseAccountItems;
  };

  // # Handle navigation to admin dashboard (employee portal)
  const handleEmployeeNavigation = useCallback(async (e?: React.MouseEvent) => {
    try {
      logDev('🚀 AppHeader: Navigating to employee dashboard');

      // Prevent any default behavior or propagation
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      
      // Check if we're on localhost - if so, use localhost URLs, otherwise use production URLs
      const isLocalhost = typeof window !== 'undefined' && 
        (window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1' || 
         window.location.hostname.startsWith('192.168.') ||
         window.location.hostname.startsWith('10.0.'));
      
      // Navigate to workspace employee portal
      if (isLocalhost) {
        window.location.href = '/workspace/employee';
      } else {
        // Preserve current domain (dev.luminawater.io or luminawater.io)
        const currentOrigin = window.location.origin; // e.g., https://dev.luminawater.io
        window.location.href = `${currentOrigin}/workspace/employee`;
      }
      
    } catch (error) {
      logDev('Error in employee navigation:', error);
      // Fallback to workspace path
      window.location.href = '/workspace/employee';
    }
  }, []);

  // Helper function to ensure href is never undefined
  const getHref = (portalPath: string, webPath: string, fallback: string = '/') => {
    if (currentApp === 'portal') {
      return portalPath;
    }
    if (currentApp === 'web') {
      return webPath;
    }
    return fallback;
  };

  // # Get My Account menu items for MegaMenu
  const getMyAccountMenuItems = (): MegaMenuItem[] => {
    // If user is not logged in, show login option
    if (!user) {
      return [
        {
          label: 'Logg Inn',
          href: authLoginUrl,
          description: 'Logg inn på din konto',
          icon: <User size={16} />,
          featured: true
        },
        {
          label: 'Registrer Deg',
          href: authSignupUrl,
          description: 'Opprett en ny konto',
          icon: <User size={16} />
        }
      ];
    }

    const menuItems: MegaMenuItem[] = [];

    // Workspace - Show prominently at top if user has admin rights
    if (hasAdminRights()) {
      const handleWorkspaceClick = async (e?: React.MouseEvent) => {
        // Prevent default link behavior
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        
        try {
          logDev.info('[AppHeader] Workspace clicked (My Account)', {
            action: 'workspace_navigation',
            source: 'my_account_menu'
          });
          const currentPlatform = detectPlatform();
          
          // For employees/workspace users: navigate to /workspace/dashboard
          // If on webstore platform, navigate on same platform
          // Otherwise, navigate to webstore platform with workspace path
          if (currentPlatform === 'webstore') {
            // Same platform - use direct navigation
            window.location.href = '/workspace/dashboard';
          } else {
            // Cross-platform navigation to webstore with workspace path
            await navigateToPlatform('webstore', '/workspace/dashboard', { 
              preserveSession: true,
              userLanguagePreference: user?.language_preference 
            });
          }
        } catch (error) {
          console.error('[AppHeader] Error navigating to workspace:', error);
          // Fallback: Try direct navigation to workspace dashboard
          window.location.href = '/workspace/dashboard';
        }
      };
      
      menuItems.push({
        label: 'Workspace',
        description: 'Gå til arbeidsområdet og administrasjon',
        icon: <Shield size={16} />,
        featured: true,
        className: 'workspace-link',
        onClick: handleWorkspaceClick
      });
    } else if (user) {
      // Portal - Show for customers (users without admin rights)
      const handlePortalClick = (e?: React.MouseEvent) => {
        // Prevent default link behavior
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        
        logDev.info('[AppHeader] Portal clicked (My Account)', {
          action: 'portal_navigation',
          source: 'my_account_menu'
        });
        
        // Navigate to customer portal
        window.location.href = '/portal';
      };
      
      menuItems.push({
        label: 'Portal',
        description: 'Gå til din kundeportal',
        icon: <User size={16} />,
        featured: true,
        className: 'portal-link',
        onClick: handlePortalClick
      });
    }

    // Profile & Account - Collapsible section with cleaner sub-styling
    menuItems.push({
      label: 'Profil & Konto',
      icon: <User size={16} />,
      children: [
        {
          label: 'Min Profil',
          href: getHref('/profile', '/portal/profile', '/profile'),
          description: 'Administrer din profilinformasjon',
          icon: <User size={16} />
        },
        {
          label: 'Kontoinnstillinger',
          href: getHref('/settings', '/portal/profile/settings', '/settings'),
          description: 'Endre preferanser, varsler og sikkerhet',
          icon: <Settings size={16} />
        }
      ]
    });

    // Orders - Collapsible section
    menuItems.push({
      label: 'Bestillinger',
      icon: <Package size={16} />,
      children: [
        {
          label: 'Mine Bestillinger',
          href: '/account/orders',
          description: 'Se og spor dine bestillinger',
          icon: <Package size={16} />
        },
        {
          label: 'Ordrehistorikk',
          href: '/account/orders',
          description: 'Full oversikt over tidligere bestillinger',
          icon: <FileText size={16} />
        }
      ]
    });

    // Wishlist & Favorites - Collapsible section
    menuItems.push({
      label: 'Ønskeliste & Favoritter',
      icon: <Heart size={16} />,
      children: [
        {
          label: 'Ønskeliste',
          href: getHref('/wishlist', '/portal/wishlist', '/wishlist'),
          description: 'Dine lagrede produkter',
          icon: <Heart size={16} />
        },
        {
          label: 'Favorittprodukter',
          href: getHref('/favorites', '/portal/favorites', '/favorites'),
          description: 'Produkter du har markert som favoritter',
          icon: <Star size={16} />
        }
      ]
    });

    // Addresses & Delivery - Collapsible section
    menuItems.push({
      label: 'Adresser & Levering',
      icon: <MapPin size={16} />,
      children: [
        {
          label: 'Leveringsadresser',
          href: getHref('/addresses', '/portal/addresses', '/addresses'),
          description: 'Administrer leverings- og faktureringsadresser',
          icon: <MapPin size={16} />
        },
        {
          label: 'Leveringsinnstillinger',
          href: getHref('/shipping', '/portal/shipping', '/shipping'),
          description: 'Foretrukne leveringsmetoder',
          icon: <Truck size={16} />
        }
      ]
    });

    // Tasks, Reminders & Notifications - Collapsible section
    menuItems.push({
      label: 'Oppgaver & Påminnelser',
      icon: <CheckSquare size={16} />,
      children: [
        {
          label: 'Mine Oppgaver',
          href: getHref('/tasks', '/portal/tasks', '/tasks'),
          description: 'Se og administrer dine oppgaver',
          icon: <CheckSquare size={16} />
        },
        {
          label: 'Påminnelser',
          href: getHref('/reminders', '/portal/reminders', '/reminders'),
          description: 'Administrer dine påminnelser',
          icon: <Clock size={16} />
        },
        {
          label: 'Varslinger',
          href: getHref('/notifications', '/portal/notifications', '/notifications'),
          description: 'Se alle dine varslinger',
          icon: <Bell size={16} />
        }
      ]
    });

    // Add premium features if applicable
    if (userRole === USER_ROLES.PREMIUM_CUSTOMER) {
      menuItems.push({
        label: 'Premium Fordeler',
        icon: <Crown size={16} />,
        children: [
          {
            label: 'Premium Funksjoner',
            href: getHref('/premium', '/portal/premium', '/premium'),
            description: 'Tilgang til eksklusive premium-funksjoner',
            icon: <Crown size={16} />
          },
          {
            label: 'Prioritert Support',
            href: getHref('/support', '/portal/support', '/support'),
            description: 'Få raskere hjelp fra kundeservice',
            icon: <Phone size={16} />
          }
        ]
      });
    }

    // App Theme - Collapsible section
    menuItems.push({
      label: 'App Tema',
      icon: <Palette size={16} />,
      className: 'theme-section',
      children: [
        {
          label: 'Temafarger',
          description: 'Velg app-tema (Bright, Zen, Night)',
          icon: <Palette size={16} />,
          onClick: () => setShowThemeSelector(true)
        }
      ]
    });

    // Support & Help - Collapsible section with separator
    menuItems.push({
      label: 'Support & Hjelp',
      icon: <HelpCircle size={16} />,
      className: 'support-section',
      children: [
        {
          label: 'Kontakt Oss',
          href: '/kontakt',
          description: 'Ta kontakt med kundeservice',
          icon: <MessageCircle size={16} />
        },
        {
          label: 'FAQ & Hjelp',
          href: '/help',
          description: 'Ofte stilte spørsmål og veiledninger',
          icon: <HelpCircle size={16} />
        }
      ]
    });

    // Logout at the bottom with special styling
    menuItems.push({
      label: 'Logg Ut',
      onClick: handleSignOut,
      description: 'Logg ut av kontoen din',
      icon: <LogOut size={16} />,
      className: 'logout'
    });

    return menuItems;
  };

  // # Get Admin dropdown items
  const getAdminItems = (): any[] => {
    if (!hasAdminRights()) return [];

    const adminItems: any[] = [];

    // Owner specific items
    if (userRole === USER_ROLES.OWNER) {
      adminItems.push(
        { label: 'Owner Panel', href: '/owner', icon: <Crown size={16} /> },
        { label: 'System Settings', href: '/owner/settings', icon: <Settings size={16} /> },
        { label: 'Billing', href: '/owner/billing', icon: <CreditCard size={16} /> },
        { label: 'Analytics', href: '/owner/analytics', icon: <BarChart3 size={16} /> },
        { divider: true }
      );
    }

    // Admin specific items - Direct link to workspace
    if (userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.OWNER) {
      // Navigate to workspace dashboard
      const handleWorkspaceClick = async (e?: React.MouseEvent) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        
        try {
          logDev.info('[AppHeader] Workspace navigation clicked (Admin items)', {
            action: 'workspace_navigation',
            source: 'admin_menu'
          });
          const currentPlatform = detectPlatform();
          
          // For employees/workspace users: navigate to /workspace/dashboard
          // If on webstore platform, navigate on same platform
          // Otherwise, navigate to webstore platform with workspace path
          if (currentPlatform === 'webstore') {
            // Same platform - use direct navigation
            window.location.href = '/workspace/dashboard';
          } else {
            // Cross-platform navigation to webstore with workspace path
            await navigateToPlatform('webstore', '/workspace/dashboard', { 
              preserveSession: true,
              userLanguagePreference: user?.language_preference 
            });
          }
        } catch (error) {
          console.error('[AppHeader] Error navigating to workspace:', error);
          // Fallback: Try direct navigation to workspace dashboard
          window.location.href = '/workspace/dashboard';
        }
      };

      adminItems.push(
        { 
          label: '🚀 Åpne Workspace', 
          icon: <Shield size={16} />,
          onClick: handleWorkspaceClick
        },
        { divider: true }
      );
    }

    // Manager specific items
    if (userRole === USER_ROLES.MANAGER || userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.OWNER) {
      adminItems.push(
        { label: 'Team Management', href: '/team', icon: <Users size={16} /> },
        { label: 'Analytics', href: '/analytics', icon: <BarChart3 size={16} /> },
        { label: 'Reports', href: '/reports', icon: <FileText size={16} /> },
        { divider: true }
      );
    }

    // Employee specific items
    if (userRole === USER_ROLES.EMPLOYEE || userRole === USER_ROLES.MANAGER || userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.OWNER) {
      adminItems.push(
        { label: 'Dashboard', href: '/dashboard', icon: <BarChart3 size={16} /> },
        { label: 'Order Management', href: '/orders', icon: <Package size={16} /> },
        { label: 'Customer Management', href: '/customers', icon: <Users size={16} /> },
        { label: 'Product Management', href: '/products', icon: <ShoppingBag size={16} /> }
      );
    }

    // Developer specific items
    if (userRole === USER_ROLES.DEVELOPER) {
      adminItems.push(
        { label: 'Dev Tools', href: '/dev', icon: <Wrench size={16} /> },
        { label: 'API Documentation', href: '/api', icon: <Settings size={16} /> },
        { label: 'System Logs', href: '/logs', icon: <FileText size={16} /> }
      );
    }

    return adminItems;
  };

  // # Get role-specific dropdown items for other roles
  const getRoleSpecificItems = (): any[] => {
    if (!userRole) return [];

    switch (userRole) {
      case USER_ROLES.RETAILER:
        return [
          { label: 'Retailer Portal', href: '/retailer', icon: <Store size={16} /> },
          { label: 'Inventory', href: '/inventory', icon: <Package size={16} /> },
          { label: 'Orders', href: '/orders', icon: <FileText size={16} /> }
        ];
      
      case USER_ROLES.SPONSOR:
        return [
          { label: 'Sponsor Portal', href: '/sponsor', icon: <Handshake size={16} /> },
          { label: 'Campaigns', href: '/campaigns', icon: <BarChart3 size={16} /> },
          { label: 'Analytics', href: '/analytics', icon: <BarChart3 size={16} /> }
        ];
      
      case USER_ROLES.DISTRIBUTOR:
        return [
          { label: 'Distributor Portal', href: '/distributor', icon: <Truck size={16} /> },
          { label: 'Inventory', href: '/inventory', icon: <Package size={16} /> },
          { label: 'Orders', href: '/orders', icon: <FileText size={16} /> }
        ];
      
      case USER_ROLES.AFFILIATE:
        return [
          { label: 'Affiliate Portal', href: '/affiliate', icon: <LinkIcon size={16} /> },
          { label: 'My Links', href: '/links', icon: <LinkIcon size={16} /> },
          { label: 'Earnings', href: '/earnings', icon: <DollarSign size={16} /> }
        ];
      
      default:
        return [];
    }
  };

  // # Memoize expensive menu calculations to improve performance
  // Use stable values (userId, userRole) instead of entire user object to prevent unnecessary recalculations
  const userId = user?.id;
  const userEmail = user?.email;
  const mainNavItems = useMemo(() => getMainNavigationItems(), [currentApp, userId, userRole, pathname]);
  const myAccountItems = useMemo(() => getMyAccountItems(), [userId, userRole, currentApp]);
  const myAccountMenuItems = useMemo(() => getMyAccountMenuItems(), [userId, userEmail, userRole, currentApp]);
  const adminMenuItems = useMemo(() => getAdminMenuItems(), [userId, userRole]);
  const adminItems = useMemo(() => getAdminItems(), [userId, userRole]);
  const roleSpecificItems = useMemo(() => getRoleSpecificItems(), [userId, userRole]);

  // Toggle section expansion in mobile drawer
  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // # Prevent hydration mismatch by waiting for translations to load
  if (!translationsReady) {
    return null;
  }

  return (
    <>
      <header className={`app-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="header-container">
          {/* Logo with Menu Buttons */}
          <div className="logo" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {/* Mobile Hamburger Menu - Shows on mobile screens, HIDDEN on portal routes */}
            {!onPortalMenuToggle && (
              <button
                className="mobile-unified-menu-btn mobile-left"
                onClick={() => setIsDrawerOpen(true)}
                aria-label="Open menu"
                title="Menu"
              >
                <Menu size={24} />
              </button>
            )}

            {/* Portal Menu Toggle - Only visible on portal routes */}
            {onPortalMenuToggle && (
              <button
                className="portal-menu-toggle-button"
                onClick={onPortalMenuToggle}
                aria-label="Toggle portal menu"
                title="Portal Menu"
              >
                <Menu size={24} />
              </button>
            )}
            
            <Link href="/" className="logo-link">
              <Image
                src="/assets/lumina-logo.png"
                alt="LuminaWater Logo"
                width={64}
                height={64}
                className="logo-icon"
                style={{ width: 'auto', height: 'auto' }}
                priority
              />
            </Link>
          </div>

          {/* Desktop Navigation with Mega Menu (lazy to reduce initial bundle) */}
          <nav className="desktop-nav">
            <Suspense fallback={null}>
              <MegaMenu 
                items={mainNavItems} 
                className="main-navigation"
                align="left"
              />
            </Suspense>
          </nav>

          {/* User Actions */}
          <div className="user-actions">

            {/* Mobile: Combined mega menu (shows on mobile only) */}
            {myAccountMenuItems.length > 0 && (() => {
              // Account menu items (already includes admin portal if applicable)
              const combinedMenuItems: MegaMenuItem[] = [
                ...myAccountMenuItems
              ];

              return (
                <div className="mobile-combined-menu">
                  <Suspense fallback={null}>
                    <MegaMenu
                      items={combinedMenuItems}
                      className="mobile-combined-navigation"
                      align="right"
                    clickableTitle={{
                      label: (
                        <div
                          className="mobile-combined-btn"
                          title="Konto"
                          style={{ position: 'relative' }}
                        >
                          <div className="mobile-combined-icons">
                            <User size={22} />
                            {hasAdminRights() && user && (
                              <Shield size={16} className="mobile-admin-icon" />
                            )}
                          </div>
                          {/* Notification badge on Konto icon */}
                          {unreadCount > 0 && (
                            <span 
                              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] font-medium"
                              style={{
                                position: 'absolute',
                                top: '-4px',
                                right: '-4px',
                                minWidth: '18px',
                                height: '18px',
                                padding: '0 4px',
                                fontSize: '10px',
                                lineHeight: '18px'
                              }}
                            >
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          )}
                        </div>
                      ) as React.ReactNode,
                      href: user ? getHref("/profile", "/portal/profile", "/profile") : authLoginUrl
                    }}
                  />
                  </Suspense>
                </div>
              );
            })()}

            {/* Desktop Notification Badge */}
            <div className="desktop-notification-badge">
              <NotificationBadge 
                variant="default"
                onNotificationClick={(notificationId) => {
                  logDev('Desktop notification clicked:', notificationId);
                }}
              />
            </div>

            {/* Desktop Locale Selector */}
            <div className="desktop-locale-selector">
              <LocaleSelector />
            </div>

            {/* Admin Quick Actions Dropdown (Admin/Owner only) - Next to account dropdown */}
            {hasAdminRights() && user && (
              <div className="desktop-admin-quick-actions">
                <AdminQuickActionsDropdown />
              </div>
            )}

            {/* Desktop: Show combined mega menu */}
            <div className="desktop-user-menus">
              {/* Combined Account & Admin MegaMenu */}
              {myAccountMenuItems.length > 0 && (() => {
                // Account menu items (already includes admin portal if applicable)
                const combinedMenuItems: MegaMenuItem[] = [
                  ...myAccountMenuItems
                ];

                return (
                  <Suspense fallback={null}>
                    <MegaMenu
                      items={combinedMenuItems}
                      className="combined-navigation"
                      align="right"
                      clickableTitle={{
                        label: (
                          <div className="combined-profile-trigger" title="Konto">
                            <div className="combined-icons">
                              <User size={18} className="user-profile-icon" />
                              {hasAdminRights() && user && (
                                <Shield size={18} className="admin-profile-icon" />
                              )}
                              <span className="combined-label desktop-only">
                                Konto
                              </span>
                            </div>
                          </div>
                        ) as React.ReactNode,
                        // # Only add href if user is logged in - if not logged in, open dropdown like LocaleSelector
                        href: user ? getHref("/profile", "/portal/profile", "/profile") : undefined
                      }}
                    />
                  </Suspense>
                );
              })()}
              
              {/* Fallback simple button if MegaMenu fails */}
              {myAccountMenuItems.length === 0 && (
                <div className="fallback-account-button">
                  <a href={authLoginUrl} className="btn btn-outline">
                    Min Konto
                  </a>
                </div>
              )}
            </div>

            {/* Profile Completion Dialog */}
            {user && (
              <ProfileCompletionDialog
                isOpen={showProfileDialog}
                onClose={() => setShowProfileDialog(false)}
                userId={user.id}
                onComplete={handleProfileComplete}
              />
            )}

            {/* Theme Color Selector Dialog */}
            <ThemeColorSelectorDialog
              isOpen={showThemeSelector}
              onClose={() => setShowThemeSelector(false)}
            />

            {/* Role-specific Dropdown */}
            {roleSpecificItems.length > 0 && (
              <Dropdown
                trigger={
                  <div
                    className="btn btn-outline"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        // The dropdown will handle the click
                      }
                    }}
                  >
                    <Store size={16} />
                    {translationsReady ? t('portal') : 'Portal'}
                  </div>
                }
                items={roleSpecificItems}
                align="right"
              />
            )}
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      <div className={`drawer-overlay ${isDrawerOpen ? 'open' : ''}`} onClick={() => setIsDrawerOpen(false)}>
        <div className={`drawer ${isDrawerOpen ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
          <div className="drawer-header">
            <div className="drawer-title">
              <Image
                src="/assets/lumina-logo.png"
                alt="LuminaWater Logo"
                width={48}
                height={48}
                className="logo-icon"
                style={{ width: 'auto', height: 'auto' }}
                priority
              />
            </div>
            <button
              className="drawer-close"
              onClick={() => setIsDrawerOpen(false)}
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>

          <div className="drawer-content">
            {/* User Info */}
            {user && (
              <div className="drawer-user">
                <div className="user-avatar">
                  <div className="user-icon-container">
                    <User size={20} />
                    {hasAdminRights() && (
                      <div className="admin-icon" title="Admin Access">
                        <Settings size={10} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="user-details">
                  <div className="user-email">{user.email}</div>
                  {userRole && (
                    <div className="user-role" data-role={userRole}>{userRole}</div>
                  )}
                </div>
                {/* Notification Badge in mobile drawer */}
                <div className="drawer-notifications">
                  <NotificationBadge 
                    variant="minimal"
                    onNotificationClick={(notificationId) => {
                      logDev('Mobile notification clicked:', notificationId);
                      setIsDrawerOpen(false);
                    }}
                  />
                </div>
              </div>
            )}

            {/* Navigation Items */}
            <nav className="drawer-nav">
              <div className="nav-section">
                <h3 className="nav-section-title">{translationsReady ? t('main') : 'Main'}</h3>
                {mainNavItems.map((item, index) => (
                  <div key={index}>
                    {item.href ? (
                  <Link 
                    href={item.href} 
                    className={`drawer-nav-link ${item.isActive ? 'active' : ''}`} 
                    onClick={() => setIsDrawerOpen(false)}
                  >
                    {item.icon}
                    <span className="nav-label">{item.label}</span>
                  </Link>
                    ) : (
                      <div className="drawer-nav-section">
                        <button
                          className="nav-section-header collapsible"
                          onClick={() => toggleSection(`main-${index}`)}
                        >
                          <div className="nav-section-title-with-icon">
                            {item.icon}
                            <span>{item.label}</span>
                          </div>
                          <span className={`expand-icon ${expandedSections.includes(`main-${index}`) ? 'expanded' : ''}`}>
                            ▼
                          </span>
                        </button>
                        {expandedSections.includes(`main-${index}`) && item.children && (
                          <div className="drawer-nav-subsection">
                            {item.children.map((child, childIndex) => (
                              <div key={childIndex} className="drawer-nav-subsection-item">
                                <div className="drawer-nav-subtitle">
                                  {child.icon}
                                  <span>{child.label}</span>
                                </div>
                                {child.children && (
                                  <div className="drawer-nav-sublinks">
                                    {child.children.map((subChild, subIndex) => (
                                      <Link
                                        key={subIndex}
                                        href={subChild.href || '#'}
                                        className="drawer-nav-sublink"
                                        onClick={() => setIsDrawerOpen(false)}
                                      >
                                        {subChild.icon}
                                        <div className="drawer-nav-sublink-content">
                                          <span className="drawer-nav-sublink-label">{subChild.label}</span>
                                          {subChild.description && (
                                            <span className="drawer-nav-sublink-description">{subChild.description}</span>
                                          )}
                                        </div>
                                        {subChild.badge && <span className="drawer-nav-sublink-badge">{subChild.badge}</span>}
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* My Account - Collapsible Section */}
              {myAccountItems.length > 0 && (
                <div className="nav-section">
                  <button
                    className="nav-section-header collapsible"
                    onClick={() => toggleSection('account')}
                  >
                    <div className="nav-section-title-with-icon">
                      <User size={18} />
                      <span>Min Konto</span>
                    </div>
                    <span className={`expand-icon ${expandedSections.includes('account') ? 'expanded' : ''}`}>
                      ▼
                    </span>
                  </button>
                  {expandedSections.includes('account') && (
                    <div className="nav-section-content">
                      {myAccountItems.filter(item => !item.divider).map((item, index) => (
                        item.onClick ? (
                          <button
                            key={index}
                            className={`drawer-nav-link ${item.label === 'Logg Ut' ? 'logout' : ''}`}
                            onClick={() => {
                              setIsDrawerOpen(false);
                              item.onClick();
                            }}
                          >
                            {item.icon}
                            <span className="nav-label">{item.label}</span>
                          </button>
                        ) : (
                          <a key={index} href={item.href} className="drawer-nav-link" onClick={() => setIsDrawerOpen(false)}>
                            {item.icon}
                            <span className="nav-label">{item.label}</span>
                          </a>
                        )
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Admin - Collapsible Section */}
              {adminItems.length > 0 && (
                <div className="nav-section">
                  <button
                    className="nav-section-header collapsible"
                    onClick={() => toggleSection('admin')}
                  >
                    <div className="nav-section-title-with-icon">
                      <Shield size={18} />
                      <span>Min Admin</span>
                    </div>
                    <span className={`expand-icon ${expandedSections.includes('admin') ? 'expanded' : ''}`}>
                      ▼
                    </span>
                  </button>
                  {expandedSections.includes('admin') && (
                    <div className="nav-section-content">
                      {adminItems.filter(item => !item.divider).map((item, index) => (
                        item.onClick ? (
                          <button
                            key={index}
                            className={`drawer-nav-link ${item.className || ''}`}
                            onClick={() => {
                              setIsDrawerOpen(false);
                              item.onClick();
                            }}
                          >
                            {item.icon}
                            <span className="nav-label">{item.label}</span>
                          </button>
                        ) : (
                          <a key={index} href={item.href} className="drawer-nav-link" onClick={() => setIsDrawerOpen(false)}>
                            {item.icon}
                            <span className="nav-label">{item.label}</span>
                          </a>
                        )
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Role-specific items */}
              {roleSpecificItems.length > 0 && (
                <div className="nav-section">
                  <h3 className="nav-section-title">{translationsReady ? t('portal') : 'Portal'}</h3>
                  {roleSpecificItems.map((item, index) => (
                    <a key={index} href={item.href} className="drawer-nav-link" onClick={() => setIsDrawerOpen(false)}>
                      {item.icon}
                      <span className="nav-label">{item.label}</span>
                    </a>
                  ))}
                </div>
              )}
            </nav>

            {/* Drawer Actions - Login/Logout handled in My Account menu */}
            <div className="drawer-actions">
              {/* Actions are now handled within the My Account menu above */}
            </div>

            {/* Webstore Search - At bottom of drawer (only for webstore) */}
            {currentApp === 'web' && (
              <div className="drawer-search" style={{ 
                padding: '1rem 1.25rem 0.75rem 1.25rem', 
                marginTop: 'auto'
              }}>
                <WebstoreSearchBox 
                  className="w-full"
                  onSelect={() => setIsDrawerOpen(false)}
                />
              </div>
            )}

            {/* Mobile Locale Selector - At bottom of drawer */}
            <div className="drawer-locale-selector" style={{ 
              padding: '0.75rem 1.25rem 1rem 1.25rem',
              marginTop: currentApp === 'web' ? '0.5rem' : 'auto'
            }}>
              <LocaleSelector />
            </div>
          </div>
        </div>
      </div>

    </>
  );
}

// Custom props comparison to avoid unnecessary re-renders
function areEqual(prev: AppHeaderProps, next: AppHeaderProps) {
  return (
    prev.currentApp === next.currentApp &&
    prev.loading === next.loading &&
    prev.userRole === next.userRole &&
    prev.user?.id === next.user?.id &&
    prev.user?.profile_completed === next.user?.profile_completed
  );
}

export default React.memo(AppHeaderComponent, areEqual);